import { AgCharts } from 'ag-charts-react'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { SelectButton } from 'primereact/selectbutton'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { SelectOption } from '../../../types/types'
import { AvailableFieldsForHeadlineData, InputFields } from '../../../utils/constants'
import { themeState } from '../../../utils/state-atoms'
import type { RunCase } from './types'

export type AverageMode = 'run' | 'filtered'
/**
 * Validates that the selected case rows represent a clean sweep of a single state/input field.
 * Returns true if valid (or if no state field is selected on either axis), false otherwise.
 */
export const checkSweepData = (xAxis: string, yAxis: string, caseRows: Record<string, any>[]) => {
  let stateField
  if (InputFields.includes(xAxis)) {
    stateField = xAxis
  }
  if (!stateField && InputFields.includes(yAxis)) {
    stateField = yAxis
  }
  if (stateField) {
    // check sweep data
    let goodSweepData = true
    // iterate all state fields
    for (let i = 0; i < InputFields.length; i++) {
      const field = InputFields[i]
      if (field === stateField) {
        continue
      }
      const fieldValue = caseRows[0][field]
      if (caseRows.some(row => fieldValue !== row[field])) {
        goodSweepData = false
        break
      }
    }
    return goodSweepData
  }

  return true
}

const attitudeStateOption: SelectOption = {
  label: 'Attitude State',
  value: 'attitude-state'
}

export type TrendsChartProps = {
  yIndex: number
  xIndex: number
  defaultXAxis?: string
  defaultYAxis?: string | string[]
  defaultAverageMode?: AverageMode
  onXAxisChange?: (row: number, col: number, xAxis: string) => void
  onYAxisChange?: (row: number, col: number, yAxis: string[]) => void
  onAverageModeChange?: (row: number, col: number, averageMode: AverageMode) => void
  headlineData: Array<Record<string, any>>
  runAverageData?: Array<Record<string, any>>
  selectedCases?: RunCase[]
}

const yOptions: SelectOption[] = Object.values(AvailableFieldsForHeadlineData)
  .filter(field => field.plottable)
  .map(({ headerName, field }) => ({
    label: headerName,
    value: field
  }))

const xOptions: SelectOption[] = [attitudeStateOption, ...yOptions]

const TrendsChart = ({
  yIndex,
  xIndex,
  defaultXAxis,
  defaultYAxis,
  defaultAverageMode = 'filtered',
  headlineData,
  runAverageData = [],
  selectedCases,
  onXAxisChange,
  onYAxisChange,
  onAverageModeChange
}: TrendsChartProps) => {
  const appTheme = useRecoilValue(themeState)
  const [chartType, setChartType] = useState<'line' | 'scatter'>('line')
  const [xAxis, setXAxis] = useState(defaultXAxis)
  const [yAxes, setYAxes] = useState<string[]>(() => {
    if (!defaultYAxis) return []
    return Array.isArray(defaultYAxis) ? defaultYAxis : [defaultYAxis]
  })
  const [xName, setXName] = useState(() => {
    return xOptions.find(option => option.value === defaultXAxis)?.label || ''
  })
  const [averageMode, setAverageMode] = useState<AverageMode>(defaultAverageMode)

  const chartOptions = useMemo(() => {
    if (!headlineData || headlineData.length === 0 || yAxes.length === 0) return null
    if (!checkSweepData(xAxis || '', yAxes[0] || '', headlineData)) {
      toast.error('Selected cases do not represent a clean sweep.', { id: 'sweep-data' })
      return null
    }
    const isAttitudeStateXAxis = xAxis === attitudeStateOption.value
    // Filter data to only include selected cases
    const validGmIds = selectedCases ? new Set(selectedCases.map(c => String(c.gmId))) : null
    const filteredData = validGmIds
      ? headlineData.filter(item => validGmIds.has(String(item.gm_id)))
      : headlineData
    if (filteredData.length === 0) return null

    const runs = Array.from(new Set(filteredData.map(item => `${item.project}::${item.run}`)))
    const selectedRunKeys = new Set(
      (selectedCases || [])
        .filter(caseItem => caseItem.project && caseItem.run)
        .map(caseItem => `${caseItem.project}::${caseItem.run}`)
    )

    // Build a globally-sorted list of all unique state names so that every run's
    // data is ordered by the same axis positions.  Without this, each run is sorted
    // independently and AG Charts connects line points in data-array order, causing
    // lines to jump back across the category axis when runs have different state sets.
    const globalStateOrder = isAttitudeStateXAxis
      ? Array.from(new Set(filteredData.map(item => String(item.state_name)))).sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        )
      : null

    const series = []
    yAxes.forEach(yAxisVal => {
      const yLabel = yOptions.find(o => o.value === yAxisVal)?.label || yAxisVal
      runs.forEach(runKey => {
        const [runProject, runName] = runKey.split('::')
        const data = filteredData
          .filter(item => `${item.project}` === runProject && `${item.run}` === runName)
          .map(item => ({
            project: item.project,
            run: item.run,
            state: `${item.state_name}`,
            caseName: `${item.run}: ${item.state_name}`,
            x: isAttitudeStateXAxis ? String(item.state_name) : Number(item[xAxis || defaultXAxis]),
            y: Number(item[yAxisVal])
          }))
          .sort((a, b) =>
            isAttitudeStateXAxis
              ? globalStateOrder.indexOf(String(a.x)) - globalStateOrder.indexOf(String(b.x))
              : Number(a.x) - Number(b.x)
          )
        const legendName = yAxes.length > 1 ? `${yLabel} - ${runName}` : runName
        series.push({
          type: chartType,
          xKey: 'x',
          yKey: 'y',
          xName: xName || 'X Axis',
          yName: yLabel,
          legendItemName: legendName,
          marker: { enabled: true },
          data,
          tooltip: {
            renderer: (params: any) => {
              return `<div class="p-2"><div class="pb-1" style="color: ${params.stroke}"><strong>${
                params.datum.run
              }<br/>${params.datum.state}</strong></div>
              ${
                xAxis !== attitudeStateOption.value
                  ? `<div><strong>${xName || xAxis || 'X Axis'}:</strong> ${params.datum.x}</div>`
                  : ''
              }
              <div><strong>${yLabel}:</strong> ${params.datum.y}</div></div>`
            }
          }
        })

        const averageSourceRows = (averageMode === 'run' ? runAverageData : filteredData).filter(
          item => {
            const itemRunKey = `${item.project}::${item.run}`
            if (itemRunKey !== runKey) {
              return false
            }
            if (averageMode !== 'run' || selectedRunKeys.size === 0) {
              return true
            }
            return selectedRunKeys.has(itemRunKey)
          }
        )
        const averageValues = averageSourceRows
          .map(item => Number(item[yAxisVal]))
          .filter(value => Number.isFinite(value))

        if (data.length > 0 && averageValues.length > 0) {
          const averageY =
            averageValues.reduce((total, value) => total + value, 0) / averageValues.length
          const averageLabel = averageMode === 'run' ? 'Run Avg' : 'Filtered Avg'
          const averageData = isAttitudeStateXAxis
            ? data.map(item => ({ ...item, y: averageY }))
            : (() => {
                const sortedData = [...data].sort((left, right) => Number(left.x) - Number(right.x))
                if (sortedData.length === 1) {
                  return [{ ...sortedData[0], y: averageY }]
                }
                return [
                  { ...sortedData[0], y: averageY },
                  { ...sortedData[sortedData.length - 1], y: averageY }
                ]
              })()

          series.push({
            type: 'line',
            xKey: 'x',
            yKey: 'y',
            xName: xName || 'X Axis',
            yName: `${yLabel} ${averageLabel}`,
            legendItemName: `${legendName} ${averageLabel}`,
            marker: { enabled: false },
            lineDash: [4, 4],
            data: averageData,
            tooltip: {
              renderer: (params: any) => {
                return `<div class="p-2"><div class="pb-1" style="color: ${params.stroke}"><strong>${
                  params.datum.run
                }<br/>${averageLabel}</strong></div><div><strong>${yLabel}:</strong> ${params.datum.y}</div></div>`
              }
            }
          })
        }
      })
    })

    const yAxisTitle =
      yAxes.length === 1
        ? yOptions.find(o => o.value === yAxes[0])?.label || yAxes[0]
        : yAxes.map(y => yOptions.find(o => o.value === y)?.label || y).join(', ')

    return {
      series: series as any,
      axes: [
        {
          type: isAttitudeStateXAxis ? 'category' : 'number',
          position: 'bottom',
          title: { text: xName || xAxis || 'X Axis' },
          label: {
            autoRotate: true
          },
          gridLine: {
            enabled: true
          },
          line: {
            enabled: true
          }
        },
        {
          type: 'number',
          position: 'left',
          title: { text: yAxisTitle },
          gridLine: {
            enabled: true
          },
          line: {
            enabled: true
          }
        }
      ],
      padding: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 30
      },
      zoom: {
        enabled: true,
        axes: 'xy'
      },
      theme: appTheme === 'dark' ? 'ag-default-dark' : 'ag-default'
    }
  }, [
    headlineData,
    xAxis,
    yAxes,
    xName,
    chartType,
    appTheme,
    selectedCases,
    defaultXAxis,
    averageMode,
    runAverageData
  ])

  return (
    <div className="h-full flex flex-col items-stretch justify-center relative p-4 pt-2 px-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center px-0 space-x-2 flex-wrap">
          <div className="flex items-center">
            <label className="mr-2">X:</label>
            <Dropdown
              value={xAxis}
              disabled={!selectedCases || selectedCases.length === 0}
              defaultValue={defaultXAxis}
              options={xOptions}
              onChange={e => {
                if (InputFields.includes(e.value) && yAxes.some(y => InputFields.includes(y))) {
                  toast.error('Plotting two state fields is not allowed.')
                  return
                }
                setXAxis(e.value)
                setXName(xOptions.find(option => option.value === e.value)?.label || '')
                onXAxisChange && onXAxisChange(yIndex, xIndex, e.value)
              }}
              placeholder="Select..."
              scrollHeight="40rem"
              pt={{
                root: { className: 'p-0 max-w-[120px]' },
                input: { className: '!p-[4px] text-sm' },
                trigger: { className: 'w-8' }
              }}
            />
          </div>

          <div className="flex items-center">
            <label className="mr-2">Y:</label>
            <MultiSelect
              value={yAxes}
              options={yOptions}
              disabled={!selectedCases || selectedCases.length === 0}
              optionDisabled={option =>
                xAxis === attitudeStateOption.value && InputFields.includes(option.value)
              }
              onChange={e => {
                const newYAxes = e.value as string[]
                if (InputFields.includes(xAxis) && newYAxes.some(y => InputFields.includes(y))) {
                  toast.error('Plotting two state fields is not allowed.')
                  return
                }
                setYAxes(newYAxes)
                onYAxisChange && onYAxisChange(yIndex, xIndex, newYAxes)
              }}
              placeholder="Select..."
              scrollHeight="40rem"
              maxSelectedLabels={2}
              pt={{
                root: { className: 'p-0 max-w-[200px]' },
                label: { className: '!p-[4px] text-sm' },
                trigger: { className: 'w-8' },
                panel: { className: 'dropdown-panel' }
              }}
            />
          </div>

          <SelectButton
            value={chartType}
            options={[
              { label: 'Line', value: 'line' },
              { label: 'Scatter', value: 'scatter' }
            ]}
            onChange={e => {
              setChartType(e.value)
            }}
            pt={{
              root: { className: 'p-0' },
              button: { className: '!p-[4px] !px-2 text-sm focus:!shadow-none !outline-none' }
            }}
          />

          <div className="flex items-center">
            <label className="mr-2 text-sm">Avg:</label>
            <SelectButton
              value={averageMode}
              options={[
                { label: 'Run', value: 'run' },
                { label: 'Filtered', value: 'filtered' }
              ]}
              onChange={e => {
                if (!e.value) return
                setAverageMode(e.value)
                onAverageModeChange?.(yIndex, xIndex, e.value)
              }}
              pt={{
                root: { className: 'p-0' },
                button: { className: '!p-[4px] !px-2 text-sm focus:!shadow-none !outline-none' }
              }}
            />
          </div>

          <Button
            label="Plot"
            disabled={!xAxis || yAxes.length === 0}
            className="!hidden p-button-sm p-button-outlined text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {}}
          />
        </div>
      </div>

      <div className="chart-container basis-0 grow min-h-0 w-full flex items-center justify-center relative mt-4">
        {chartOptions ? (
          <div className="w-full h-full">
            <AgCharts options={chartOptions as any} />
          </div>
        ) : (
          <p className="p-10 text-center">
            No data to display.
            <br />
            {selectedCases.length === 0
              ? 'Please select at least one case to view data.'
              : !headlineData
              ? 'Please select X and Y axes.'
              : headlineData.length === 0
              ? 'Chart data is empty.'
              : ''}
          </p>
        )}
      </div>
    </div>
  )
}

export default TrendsChart
